'use client';

import { Stack } from '@mui/material';
import { useMemo } from 'react';

import { AbgTypography, FormError, useTranslation } from '@abg/kit-base';

import { useCountdown } from '../../../hooks';
import {
  LoginStep,
  OtpMethod,
} from '../../../shared/constants/login-form.constants';
import {
  LoginFormI18n,
  OtpUserData,
} from '../../../shared/types/LoginFrom.interface';
import { useLoginJourneyContext } from '../LoginJourneyContext';
import { appendFormTypeParameter } from '../utils';

import { LoginInputView, LoginInputViewFormData } from './LoginInputView';
import { LoginOtpFormProps } from './types';

export const LoginOtpForm = ({ partProps = {}, model }: LoginOtpFormProps) => {
  const { loginJourney } = useLoginJourneyContext();
  const t = useTranslation(model.i18n);
  const { count, isExpired, reset } = useCountdown({
    end: model.loginResendTimeout,
    disabled:
      loginJourney?.loginStep === LoginStep.EnterPassword ||
      loginJourney?.otpData?.formTerminated ||
      loginJourney?.otpData?.resendDisabled,
  });

  const {
    isLoading,
    errorData,
    loginStep,
    otpData,
    otpNotAvailable,
    handleSubmitOtp,
    handleResendOtp,
    handleOtherOptionsOtp,
    handleSwitchToPassword,
    handleSubmitPassword,
    handleSwitchToOtp,
  } = loginJourney;

  const viewConfig = useMemo(
    () =>
      getViewConfig({
        loginStep,
        model,
        otpData,
        otpNotAvailable,
        t,
        handleSubmitOtp,
        handleResendOtp,
        handleOtherOptionsOtp,
        handleSwitchToPassword,
        handleSubmitPassword,
        handleSwitchToOtp,
        reset,
      }),
    [
      loginStep,
      model,
      otpData,
      otpNotAvailable,
      t,
      handleResendOtp,
      handleOtherOptionsOtp,
      handleSubmitOtp,
      handleSubmitPassword,
      handleSwitchToPassword,
      handleSwitchToOtp,
      reset,
    ],
  );

  return (
    <Stack direction="column" rowGap={2}>
      {!!viewConfig['description'] && !otpData?.formTerminated && (
        <AbgTypography
          component="p"
          variant="bodySmallRegular"
          htmlContent={viewConfig['description'] as string}
        />
      )}

      {errorData && (
        <FormError
          styleParts={partProps.formError?.styleParts}
          config={errorData}
          testIdPrefix="login-otp-form"
        />
      )}

      {!otpData?.formTerminated && (
        <LoginInputView
          key={loginStep}
          model={model}
          isLoading={isLoading}
          subDescriptionText={t('mfa.modal.screen.enterCode.resendRetry.text', {
            seconds: count,
          })}
          hideCounter={
            isExpired ||
            loginStep === LoginStep.EnterPassword ||
            otpData?.resendDisabled
          }
          isPassword={loginStep === LoginStep.EnterPassword}
          testIdPrefix="login-otp-form"
          {...viewConfig}
        />
      )}
    </Stack>
  );
};

type ViewConfigParams = {
  loginStep: LoginStep | null;
  model: LoginOtpFormProps['model'];
  otpData: OtpUserData | null;
  otpNotAvailable: boolean;
  t: ReturnType<typeof useTranslation<LoginFormI18n>>;
  handleSubmitOtp: (code: string) => void;
  handleResendOtp: () => void;
  handleOtherOptionsOtp: () => Promise<void>;
  handleSwitchToPassword: () => void;
  reset: () => void;
  handleSubmitPassword: (value: string) => void;
  handleSwitchToOtp: () => Promise<void>;
};

/**
 * Builds the view configuration for the OTP-via-SMS screen.
 *
 * Context: The user chose (or was defaulted to) passwordless login. The system
 * sent a one-time code via SMS to the user's phone. This config renders:
 * - A description showing the masked phone number the code was sent to.
 * - An input field for the verification code.
 * - A "Resend" button (hidden when resend is disabled).
 * - A link to switch delivery to email (visible only when another method is available).
 * - A "Log in with password" fallback link.
 *
 * Flow: OTP (passwordless login) → phone delivery channel.
 */
function getOtpPhoneViewConfig(params: ViewConfigParams) {
  return {
    description: params.t(
      'mfa.modal.screen.enterCode.phoneNumber.description',
      {
        phoneNumber: params.otpData?.phone || '',
      },
    ),
    inputLabel: params.t('mfa.modal.screen.enterCode.codeField.title'),
    confirmBtnText: params.t('global.button.continue.label'),
    rejectBtnText: params.otpData?.resendDisabled
      ? ''
      : params.t('mfa.modal.screen.enterCode.resend.title'),
    primaryLinkText:
      params.otpData?.otherMethodAvailable && !params.otpData?.resendDisabled
        ? params.t('mfa.modal.screen.enterCode.receive.email.title')
        : '',
    secondaryLinkText: params.t('mfa.modal.screen.enterCode.login.title'),
    onSecondaryLinkClick: params.handleSwitchToPassword,
    onSubmit: (data: LoginInputViewFormData) =>
      params.handleSubmitOtp(data.inputValue),
    onReject: () => {
      params.handleResendOtp();
      params.reset();
    },
    onPrimaryLinkClick: async () => {
      await params.handleOtherOptionsOtp();
      params.reset();
    },
  };
}

/**
 * Builds the view configuration for the OTP-via-email screen.
 *
 * Context: The user chose (or was defaulted to) passwordless login. The system
 * sent a one-time code to the user's email address. This config renders:
 * - A description showing the masked email the code was sent to.
 * - An input field for the verification code.
 * - A "Resend" button (hidden when resend is disabled).
 * - A link to switch delivery to phone (visible only when another method is available).
 * - A "Log in with password" fallback link.
 *
 * Flow: OTP (passwordless login) → email delivery channel.
 */
function getOtpEmailViewConfig(params: ViewConfigParams) {
  return {
    description: params.t('mfa.modal.screen.enterCode.email.description', {
      email: params.otpData?.email || '',
    }),
    inputLabel: params.t('mfa.modal.screen.enterCode.codeField.title'),
    confirmBtnText: params.t('global.button.continue.label'),
    rejectBtnText: params.otpData?.resendDisabled
      ? ''
      : params.t('mfa.modal.screen.enterCode.resend.title'),
    primaryLinkText:
      params.otpData?.otherMethodAvailable && !params.otpData?.resendDisabled
        ? params.t('mfa.modal.screen.enterCode.receive.phone.title')
        : '',
    secondaryLinkText: params.t('mfa.modal.screen.enterCode.login.title'),
    onSecondaryLinkClick: params.handleSwitchToPassword,
    onSubmit: (data: LoginInputViewFormData) =>
      params.handleSubmitOtp(data.inputValue),
    onReject: () => {
      params.handleResendOtp();
      params.reset();
    },
    onPrimaryLinkClick: async () => {
      await params.handleOtherOptionsOtp();
      params.reset();
    },
  };
}

/**
 * Builds the view configuration for the MFA-via-SMS screen.
 *
 * Context: The user already submitted a valid password. The system now requires
 * a second factor (multi-factor authentication) and sent a code via SMS.
 * Unlike the OTP flow, there is NO "log in with password" fallback here because
 * the password step is already completed. This config renders:
 * - A description showing the masked phone number the code was sent to.
 * - An input field for the MFA verification code.
 * - A "Resend" button (hidden when resend is disabled).
 * - A link to switch delivery to email (visible only when another method is available).
 *
 * Flow: MFA (post-password second factor) → phone delivery channel.
 */
function getMfaPhoneViewConfig(params: ViewConfigParams) {
  return {
    description: params.t('login.screen.enterCode.phoneNumber.description', {
      phoneNumber: params.otpData?.phone || '',
    }),
    inputLabel: params.t('login.screen.enterCode.codeField.title'),
    confirmBtnText: params.t('global.button.continue.label'),
    rejectBtnText: params.otpData?.resendDisabled
      ? ''
      : params.t('mfa.modal.screen.enterCode.resend.title'),
    primaryLinkText:
      params.otpData?.otherMethodAvailable && !params.otpData?.resendDisabled
        ? params.t('mfa.modal.screen.enterCode.receive.email.title')
        : '',
    onSubmit: (data: LoginInputViewFormData) =>
      params.handleSubmitOtp(data.inputValue),
    onReject: () => {
      params.handleResendOtp();
      params.reset();
    },
    onPrimaryLinkClick: async () => {
      await params.handleOtherOptionsOtp();
      params.reset();
    },
  };
}

/**
 * Builds the view configuration for the MFA-via-email screen.
 *
 * Context: The user already submitted a valid password. The system now requires
 * a second factor (multi-factor authentication) and sent a code to the user's email.
 * Unlike the OTP flow, there is NO "log in with password" fallback here because
 * the password step is already completed. This config renders:
 * - A description showing the masked email the code was sent to.
 * - An input field for the MFA verification code.
 * - A "Resend" button (hidden when resend is disabled).
 * - A link to switch delivery to phone (visible only when another method is available).
 *
 * Flow: MFA (post-password second factor) → email delivery channel.
 */
function getMfaEmailViewConfig(params: ViewConfigParams) {
  return {
    description: params.t('login.screen.enterCode.email.description', {
      email: params.otpData?.email || '',
    }),
    inputLabel: params.t('login.screen.enterCode.codeField.title'),
    confirmBtnText: params.t('global.button.continue.label'),
    rejectBtnText: params.otpData?.resendDisabled
      ? ''
      : params.t('mfa.modal.screen.enterCode.resend.title'),
    primaryLinkText:
      params.otpData?.otherMethodAvailable && !params.otpData?.resendDisabled
        ? params.t('mfa.modal.screen.enterCode.receive.phone.title')
        : '',
    onSubmit: (data: LoginInputViewFormData) =>
      params.handleSubmitOtp(data.inputValue),
    onReject: () => {
      params.handleResendOtp();
      params.reset();
    },
    onPrimaryLinkClick: async () => {
      await params.handleOtherOptionsOtp();
      params.reset();
    },
  };
}

/**
 * Builds the view configuration for the password entry screen.
 *
 * Context: The user is on the traditional password login step — either as the
 * primary login method, or because they clicked "Log in with password" from an
 * OTP screen. This config renders:
 * - A password input field.
 * - A "Continue" submit button.
 * - A "Forgot password" link (opens the password reset flow).
 * - A "Log in with code" link to switch back to OTP (hidden when OTP is
 *   unavailable or resend is disabled).
 *
 * Flow: Password entry (primary or fallback from OTP).
 */
function getPasswordViewConfig(params: ViewConfigParams) {
  return {
    inputLabel: params.model.passwordFieldTitle,
    confirmBtnText: params.t('mfa.modal.screen.enterPassword.continue.title'),
    primaryLinkText:
      params.otpNotAvailable || params.otpData?.resendDisabled
        ? ''
        : params.t('mfa.modal.screen.enterPassword.login.title'),
    secondaryLinkText: params.model.separateForgotPasswordText,
    secondaryLinkHref: appendFormTypeParameter(
      params.model.forgotUsernameOrPasswordLink,
      'reset_password',
    ),
    secondaryLinkTarget: params.model.forgotUsernameOrPasswordTarget,
    onSubmit: (data: LoginInputViewFormData) =>
      params.handleSubmitPassword(data.inputValue),
    onPrimaryLinkClick: async () => {
      await params.handleSwitchToOtp();
      params.reset();
    },
  };
}

type ViewConfig = Record<string, unknown>;

/**
 * Strategy map that resolves the current login step + delivery method into the
 * correct view configuration builder.
 *
 * How it works (Strategy Pattern):
 * - Each key is a composite string: `"<LoginStep>|<OtpMethod>"` for code-entry
 *   screens, or just `"<LoginStep>"` for the password screen.
 * - Each value is one of the `get*ViewConfig` functions above.
 * - At runtime, `getViewConfig` builds a key from the current state, looks it
 *   up here, and calls the matched handler to produce the UI configuration.
 *
 * This avoids a chain of if/else or switch statements — adding a new login step
 * or delivery method only requires adding one entry to this map plus a new
 * config builder function.
 */
const viewConfigHandlers: Record<
  string,
  (params: ViewConfigParams) => ViewConfig
> = {
  // OTP flow cases
  [`${LoginStep.EnterOTP}|${OtpMethod.Phone}`]: getOtpPhoneViewConfig,
  [`${LoginStep.EnterOTP}|${OtpMethod.Email}`]: getOtpEmailViewConfig,
  // New MFA flow cases
  [`${LoginStep.EnterMFA}|${OtpMethod.Phone}`]: getMfaPhoneViewConfig,
  [`${LoginStep.EnterMFA}|${OtpMethod.Email}`]: getMfaEmailViewConfig,
  // Regular password case
  [LoginStep.EnterPassword]: getPasswordViewConfig,
};

function buildViewConfigKey(
  loginStep: LoginStep | null,
  otpMethod: OtpMethod | null | undefined,
): string {
  if (loginStep === LoginStep.EnterPassword) {
    return LoginStep.EnterPassword;
  }

  return `${loginStep}|${otpMethod}`;
}

/**
 * Resolves and returns the fully populated view configuration for the current
 * login step.
 *
 * What it does, step by step:
 * 1. Builds a lookup key from the current `loginStep` and `otpMethod`
 *    (e.g. `"EnterOTP|phone"`, `"EnterMFA|email"`, or `"EnterPassword"`).
 * 2. Looks up the key in `viewConfigHandlers` (the strategy map above).
 * 3. If a matching handler exists, calls it with all runtime params (translations,
 *    callbacks, user data) and returns the resulting config object.
 * 4. If no handler matches (unexpected state), returns an empty object — the UI
 *    will render nothing rather than crash.
 *
 * The returned config is consumed directly by `<LoginInputView>` to render the
 * correct labels, buttons, links, and callbacks for whichever screen is active.
 */
function getViewConfig(params: ViewConfigParams) {
  const key = buildViewConfigKey(params.loginStep, params.otpData?.method);

  const handler = viewConfigHandlers[key];

  return handler ? handler(params) : {};
}
